import { PASSIVES, passiveById } from './passives';
import { WEAPON_POOL } from './weapons-pool';
import { protocolById } from './protocols';
import { improviseById } from './improvise';
import { evolutionById } from './evolutions';
import { unionById } from './unions';
/* ============================================================
   稀有度分層與時間偏移（2026-08-04 P1）
   ============================================================

   `03-in-run-progression.md` 第三節的表，照抄：

     稀有度   0–5 分   5–12 分   12–20 分   強度定位
     普通      65%      45%       25%       線性小幅加成
     稀有      28%      38%       40%       明顯加成或條件效果
     史詩       6%      14%       25%       行為改變
     傳說       1%       3%       10%       質變／破格效果

   ## 為什麼分層寫在一張集中表，而不是每個項目自己帶欄位

   跟 `sim/rules.ts` 的 `TABLE` 同一個理由：**平衡的時候要一眼看完**。
   散在四個資料檔裡的話，「現在史詩有幾個」這個問題要開四個地方數，
   而那正是調稀有度時每一次都要問的問題。

   ## 分層是規則推導不是逐項手挑

   預設值由**既有欄位**決定，只有例外才列進 `OVERRIDE`：

     協定（`data/protocols.ts`）      → 傳說   帶代價的質變
     規則類被動（`kind === '規則'`）  → 史詩   改的是「怎麼玩」不是數字
     池子武裝                          → 稀有   build 的骨幹，一場拿得到 5 把
     其餘被動                          → 普通   線性加成

   手挑會漂移成「我覺得這個比較強」，而那種判斷沒有任何測試守得住。
   例外一律要在 `OVERRIDE` 裡寫一句理由。

   ## 權重不是直接乘目標比例（這裡最容易寫錯）

   目標表講的是**抽出來的稀有度分佈**，不是每個項目的乘數。普通有 15 個、
   傳說只有 6 個，直接把 65% 與 1% 當乘數的話，普通那一堆會拿到 15×65
   的總權重、傳說拿 6×1 —— 實際分佈會比目標更偏向數量多的那一層。
   所以每個項目的稀有度權重是 **目標比例 ÷ 該層當下可用的項目數**，
   總和才等於目標比例。見 `rarityWeight()`。 */

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export var RARITIES: Rarity[] = ['common', 'rare', 'epic', 'legendary'];

/** 三個時段的目標分佈（03 的表）。`t` 是這一段的**結束**分鐘數。 */
export var RARITY_BANDS: { t: number, w: Record<Rarity, number> }[] = [
  { t: 5, w: { common: 0.65, rare: 0.28, epic: 0.06, legendary: 0.01 } },
  { t: 12, w: { common: 0.45, rare: 0.38, epic: 0.14, legendary: 0.03 } },
  { t: 1e9, w: { common: 0.25, rare: 0.40, epic: 0.25, legendary: 0.10 } }
];

/** 規則推導以外的例外。**每一條都要有理由** —— 沒理由的例外就是手挑。 */
var OVERRIDE: Record<string, Rarity> = {
  /* 滿級多射一發是**離散質變**，不是百分比（見 p-multi 的註解）。 */
  'p-multi': 'epic',
  /* 復活是二元事實（有沒有第二條命），VS 的 Tiragisú 同樣是稀有貨。 */
  'p-revive': 'epic',
  /* 暴擊改分布不改期望，行為感受明顯但不改玩法 —— 規則類裡最溫和的一個。 */
  'p-crit': 'rare',
  /* 只影響寶箱件數，強度低但體感好（`05-meta-progression.md` 點名的那種）。 */
  'p-luck': 'rare',
  /* 掉修復包的機率，溫和的續戰力。 */
  'p-salvage': 'rare'
};

/** 這一項是哪一層。查不到的東西當普通 —— 抽卡不該因為漏標而爆掉。 */
export function rarityOf(id): Rarity {
  var o = OVERRIDE[id];
  if (o) return o;
  if (protocolById(id)) return 'legendary';
  /* 應急改裝（`data/improvise.ts`）＝普通。它是純數值、無限級、
     而且只在可選池見底之後出現 —— 給它稀有度只會讓報表的分佈失真
     （那張表問的是「這一輪的候選裡各層各有幾個」，而這一層沒有分層）。 */
  if (improviseById(id)) return 'common';
  var p = passiveById(id);
  if (p) return p.kind === '規則' ? 'epic' : 'common';
  /* 質變（進化／融合）＝傳說。**2026-08-05 加**：在這之前它們只有一級、
     只從寶箱出，所以走不到這張表；現在第 2、3 級會出現在升級選單裡。

     傳說而不是史詩是刻意的。傳說在 0–5 分只有 1%、5–12 分 3%，
     所以質變等級**不會跟前期的 build 成長搶名額**（這正是它被選為出口的理由：
     它有前置條件，天然只在中後期成立）；而 12 分之後它是 10%，
     加上池子那時本來就快空了（該層沒有候選時 `rarityWeight` 回 0，
     權重會自然收斂到還有東西的那幾層），後期它就是主要的供給。 */
  if (evolutionById(id) || unionById(id)) return 'legendary';
  return 'rare';
}

/** 這個時間點的目標分佈。`tMin` = 局內分鐘數。 */
export function bandAt(tMin: number): Record<Rarity, number> {
  for (var i = 0; i < RARITY_BANDS.length; i++) {
    if (tMin < RARITY_BANDS[i].t) return RARITY_BANDS[i].w;
  }
  return RARITY_BANDS[RARITY_BANDS.length - 1].w;
}

/** 每個項目的稀有度權重。

    `counts` 是**這一次抽卡的候選池**裡各層各有幾個 —— 用當下可用的數量
    而不是資料檔的總數：滿級與滿槽的項目已經被 `availableIds()` 濾掉了，
    用總數當分母的話，「這一層剩下的東西」會被系統性低估。

    `luck` 只乘史詩與傳說（03 原話：「luck 應乘算影響史詩與傳說的權重」）。
    普通與稀有不乘 —— 都乘等於沒乘。 */
export function rarityWeight(r: Rarity, tMin: number, luck: number, counts: Record<Rarity, number>) {
  var n = counts[r] || 0;
  if (!n) return 0;
  var w = bandAt(tMin)[r] / n;
  if (r === 'epic' || r === 'legendary') w *= (luck > 0 ? luck : 1);
  return w;
}

/** 給報表用：一組 id 各屬哪一層的計數。 */
export function countByRarity(ids: string[]): Record<Rarity, number> {
  var c: any = { common: 0, rare: 0, epic: 0, legendary: 0 };
  for (var i = 0; i < ids.length; i++) c[rarityOf(ids[i])]++;
  return c;
}

/** 給測試與報表用：資料檔裡各層總共有幾個（不看是否可拿）。 */
export function rarityCensus(): Record<Rarity, number> {
  var ids: string[] = [], i;
  for (i = 0; i < WEAPON_POOL.length; i++) ids.push(WEAPON_POOL[i].id);
  for (i = 0; i < PASSIVES.length; i++) ids.push(PASSIVES[i].id);
  return countByRarity(ids);
}
